import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Send, FileText, X, CheckCircle2, Eye, Pencil, Receipt as ReceiptIcon, Mail } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";
import {
  fetchInvoices, createInvoice, updateInvoice, deleteInvoice, fetchProducts, fetchClients, money, formatDate, resendReceipt,
} from "../../lib/api";
import PayPalPayButton from "../common/PayPalPayButton";
import Receipt from "../common/Receipt";
import { useAuth } from "../../context/AuthContext";

const statusColor = (s) =>
  ({
    draft: "bg-gray-100 text-gray-600",
    sent: "bg-blue-100 text-blue-700",
    paid: "bg-green-100 text-green-700",
    void: "bg-red-50 text-red-500",
  }[s] || "bg-gray-100 text-gray-600");

const emptyForm = {
  client_name: "", client_email: "", tax_rate: 0, notes: "", status: "draft",
  items: [{ description: "", quantity: 1, unit_price: 0, product_id: null }],
};

export default function Invoices() {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState("all");
  const [showReceipt, setShowReceipt] = useState(false);
  const [resending, setResending] = useState(false);

  const load = async () => {
    try {
      const inv = await fetchInvoices();
      setInvoices(inv);
      setProducts(await fetchProducts().catch(() => []));
      setClients(await fetchClients().catch(() => []));
    } catch (e) {
      toast.error("Could not load invoices");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pickClient = (name) => {
    const c = clients.find((x) => x.name === name);
    setForm((f) => ({ ...f, client_name: name, client_email: c?.email || f.client_email }));
  };

  const subtotal = form.items.reduce((s, i) => s + (Number(i.unit_price) || 0) * (Number(i.quantity) || 0), 0);
  const tax = subtotal * (Number(form.tax_rate) || 0) / 100;
  const total = subtotal + tax;

  const setItem = (idx, patch) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0, product_id: null }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const pickProduct = (idx, productId) => {
    const p = products.find((x) => x.id === productId);
    if (p) setItem(idx, { product_id: p.id, description: p.name, unit_price: p.price });
  };

  const openNew = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };

  const openEdit = (inv) => {
    setEditingId(inv.id);
    setForm({
      client_name: inv.client_name || "",
      client_email: inv.client_email || "",
      tax_rate: inv.tax_rate || 0,
      notes: inv.notes || "",
      status: inv.status || "draft",
      items: (inv.items && inv.items.length)
        ? inv.items.map((i) => ({
            description: i.description || "",
            quantity: i.quantity || 1,
            unit_price: i.unit_price || 0,
            product_id: i.product_id || null,
          }))
        : [{ description: "", quantity: 1, unit_price: 0, product_id: null }],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.client_name) { toast.error("Client name is required"); return; }
    if (!form.items.length || !form.items.some((i) => i.description)) { toast.error("Add at least one line item"); return; }
    const payload = {
      ...form,
      tax_rate: Number(form.tax_rate) || 0,
      items: form.items
        .filter((i) => i.description)
        .map((i) => ({ ...i, quantity: Number(i.quantity) || 1, unit_price: Number(i.unit_price) || 0 })),
    };
    try {
      if (editingId) await updateInvoice(editingId, payload);
      else await createInvoice(payload);
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      await load();
      toast.success(editingId ? "Invoice updated" : "Invoice created");
    } catch (e) {
      toast.error(editingId ? "Could not update invoice" : "Could not create invoice");
    }
  };

  const markSent = async (inv) => {
    await updateInvoice(inv.id, { ...inv, status: "sent" });
    load();
    toast.success(`${inv.number} marked as sent`);
  };
  const remove = async (id) => { await deleteInvoice(id); load(); toast.success("Invoice deleted"); };

  const filtered = invoices.filter((i) => filter === "all" || i.status === filter);
  const totalPaid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);
  const outstanding = invoices.filter((i) => i.status !== "paid" && i.status !== "void").reduce((s, i) => s + i.total, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy">Invoices</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {money(totalPaid)} collected · {money(outstanding)} outstanding
          </p>
        </div>
        <Button onClick={openNew} className="bg-navy hover:bg-navy-light text-white">
          <Plus className="h-4 w-4 mr-1.5" /> New Invoice
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all", "draft", "sent", "paid", "void"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3.5 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${filter === s ? "bg-navy text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream">
                <tr className="text-left text-gray-500">
                  <th className="px-5 py-3.5 font-medium">Invoice</th>
                  <th className="px-5 py-3.5 font-medium">Client</th>
                  <th className="px-5 py-3.5 font-medium hidden sm:table-cell">Total</th>
                  <th className="px-5 py-3.5 font-medium">Status</th>
                  <th className="px-5 py-3.5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr key={inv.id} className="border-t border-gray-50">
                    <td className="px-5 py-4 font-medium text-navy">{inv.number}</td>
                    <td className="px-5 py-4">
                      <div className="text-navy">{inv.client_name}</div>
                      <div className="text-xs text-gray-400">{inv.client_email}</div>
                    </td>
                    <td className="px-5 py-4 font-medium text-navy hidden sm:table-cell">{money(inv.total)}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColor(inv.status)}`}>{inv.status}</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setViewing(inv)} title="View" className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-navy/5 hover:text-navy transition-colors">
                          <Eye className="h-4 w-4" />
                        </button>
                        {inv.status !== "paid" && (
                          <button onClick={() => openEdit(inv)} title="Edit" className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-navy/5 hover:text-navy transition-colors">
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        {inv.status === "draft" && (
                          <button onClick={() => markSent(inv)} title="Mark as sent" className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                            <Send className="h-4 w-4" />
                          </button>
                        )}
                        {inv.status === "paid" && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                        <button onClick={() => remove(inv.id)} title="Delete" className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="5" className="px-5 py-12 text-center text-gray-400">No invoices found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / edit invoice dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditingId(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-navy">{editingId ? "Edit Invoice" : "New Invoice"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <Select value={form.client_name || ""} onValueChange={pickClient}>
                <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Select a client *" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  {form.client_name && !clients.some((c) => c.name === form.client_name) && (
                    <SelectItem value={form.client_name}>{form.client_name}</SelectItem>
                  )}
                  {clients.length === 0 && !form.client_name && (
                    <div className="px-2 py-1.5 text-sm text-gray-400">No clients yet — add one in Clients</div>
                  )}
                </SelectContent>
              </Select>
              <Input placeholder="Client email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">Line Items</div>
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-12 sm:col-span-5">
                    <Select value={it.product_id || ""} onValueChange={(v) => pickProduct(idx, v)}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Pick service (optional)" /></SelectTrigger>
                      <SelectContent>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {money(p.price)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="mt-1 h-9" placeholder="Description" value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })} />
                  </div>
                  <Input className="col-span-4 sm:col-span-2 h-9" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} />
                  <Input className="col-span-5 sm:col-span-3 h-9" type="number" step="0.01" placeholder="Unit price" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: e.target.value })} />
                  <div className="col-span-3 sm:col-span-2 flex justify-end">
                    <button onClick={() => removeItem(idx)} className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={addItem} className="h-9 text-sm"><Plus className="h-4 w-4 mr-1" /> Add line</Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 items-start">
              <Textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
              <div className="bg-cream rounded-lg p-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-medium">{money(subtotal)}</span></div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 flex items-center gap-1">Tax
                    <Input type="number" step="0.1" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className="h-7 w-16 ml-1" />%
                  </span>
                  <span className="font-medium">{money(tax)}</span>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-1.5 mt-1"><span className="font-semibold text-navy">Total</span><span className="font-serif text-lg font-bold text-navy">{money(total)}</span></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setEditingId(null); }}>Cancel</Button>
            <Button onClick={save} className="bg-navy hover:bg-navy-light text-white"><FileText className="h-4 w-4 mr-1.5" /> {editingId ? "Save Changes" : "Create Invoice"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View invoice dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-navy flex items-center justify-between">
                  <span>{viewing.number}</span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColor(viewing.status)}`}>{viewing.status}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="py-2 space-y-4">
                <div className="flex justify-between text-sm">
                  <div>
                    <div className="text-gray-400 text-xs uppercase tracking-wide">Billed To</div>
                    <div className="font-medium text-navy">{viewing.client_name}</div>
                    {viewing.client_email && <div className="text-gray-500">{viewing.client_email}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-gray-400 text-xs uppercase tracking-wide">Date</div>
                    <div className="text-gray-600">{formatDate(viewing.created_at)}</div>
                  </div>
                </div>

                <div className="border border-gray-100 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-cream text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Item</th>
                        <th className="text-center px-3 py-2 font-medium">Qty</th>
                        <th className="text-right px-3 py-2 font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewing.items?.map((it, i) => (
                        <tr key={i} className="border-t border-gray-50">
                          <td className="px-3 py-2 text-navy">{it.description}</td>
                          <td className="px-3 py-2 text-center text-gray-600">{it.quantity}</td>
                          <td className="px-3 py-2 text-right font-medium text-navy">{money(it.unit_price * it.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{money(viewing.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Tax ({viewing.tax_rate || 0}%)</span><span>{money(viewing.tax)}</span></div>
                  <div className="flex justify-between border-t border-gray-100 pt-1.5"><span className="font-semibold text-navy">Total</span><span className="font-serif text-lg font-bold text-navy">{money(viewing.total)}</span></div>
                </div>

                {viewing.notes && (
                  <div className="bg-cream rounded-lg p-3 text-sm text-gray-600">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Notes</div>
                    {viewing.notes}
                  </div>
                )}

                {viewing.status === "paid" && (
                  <div className="border-t border-gray-100 pt-4" data-testid="crm-receipt-section">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs uppercase tracking-wide text-gray-400">Receipt</div>
                      <span className="text-xs text-green-600 font-medium">Paid {viewing.paid_at ? formatDate(viewing.paid_at) : ""}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => setShowReceipt(true)} className="border-green-200 text-green-700 hover:bg-green-50" data-testid="crm-view-receipt">
                        <ReceiptIcon className="h-4 w-4 mr-1.5" /> View / Print Receipt
                      </Button>
                      <Button
                        size="sm" variant="outline" disabled={resending}
                        onClick={async () => {
                          setResending(true);
                          try {
                            const r = await resendReceipt(token, viewing.id);
                            toast.success(`Receipt emailed to ${r.sent_to}`);
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Could not send receipt");
                          } finally { setResending(false); }
                        }}
                        data-testid="crm-resend-receipt"
                      >
                        {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1.5" /> Email Receipt</>}
                      </Button>
                    </div>
                  </div>
                )}

                {viewing.status !== "paid" && viewing.status !== "void" && (
                  <div className="border-t border-gray-100 pt-4" data-testid="crm-collect-payment">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Collect Payment</div>
                    <PayPalPayButton
                      invoiceId={viewing.id}
                      onPaid={async () => { await load(); setViewing((v) => v && { ...v, status: "paid" }); }}
                    />
                  </div>
                )}
              </div>
              <DialogFooter>
                {viewing.status !== "paid" && (
                  <Button variant="outline" onClick={() => { const v = viewing; setViewing(null); openEdit(v); }}>
                    <Pencil className="h-4 w-4 mr-1.5" /> Edit
                  </Button>
                )}
                <Button onClick={() => setViewing(null)} className="bg-navy hover:bg-navy-light text-white">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {showReceipt && viewing && <Receipt invoice={viewing} onClose={() => setShowReceipt(false)} />}
    </div>
  );
}
